import { useEffect } from 'react';
import { Button } from '@cowbell/ui';
import { PubSub } from '../../../utils/eventUtils';
import { Modal } from '../../../components/modals/v2/helpers/v2.modal.helpers';
import { PrivacyConsentConfig } from '../../../components/modals/PrivacyConsent';
import { DeleteConnectorModalConfig } from './DeleteConnectorModal';
import ConnectorCard from '../ConnectorCard';
import ConnectorActionsMenu from '../ConnectorActionsMenu';
import { connectionStatusBadge, syncSubtitle } from '../connectors.view';

export type DataProps = {
  id?: string;
  name: string; // vendor
  vendor_name?: string;
  connectorType: string;
  description: string;
  status?: string;
  logoUrl?: string;
  lastPull?: string | null;
};

interface LeenConnectorTileProps {
  readonly data: DataProps;
  readonly handleClick: (val: string) => void;
  readonly tag?: string;
}

export const LeenConnectorTile = ({
  data,
  handleClick,
  tag,
}: LeenConnectorTileProps) => {
  const {
    name = '', // vendor
    vendor_name,
    description,
    status = 'Available',
    connectorType,
    logoUrl,
    lastPull,
  } = data;
  const displayName = vendor_name ?? name;
  const isConnected = status === 'Connected';

  // Shows privacy consent modal
  const handleAvailableClick = () => {
    Modal.show(PrivacyConsentConfig, {
      data: {
        data: {
          ...data,
          connectorType,
          connectorName: name,
        },
      },
    });
  };

  // Shows delete connector modal
  const handleDisconnect = () => {
    Modal.show(DeleteConnectorModalConfig, {
      data: {
        connection_id: data.id,
        name,
      },
    });
  };

  useEffect(() => {
    if (status === 'Available') {
      const sub = PubSub.subscribe('privacyConsent', (eventData: any) => {
        if (
          eventData.connectorType == connectorType &&
          eventData.connectorName == name
        ) {
          // LeenOnRamp renders outside the shared dialog host, off page state,
          // so nothing replaces the consent dialog — dismiss it or it covers
          // the OnRamp.
          Modal.hide();
          handleClick(connectorType);
        }
      });
      return () => {
        sub.remove();
      };
    }
  }, [name, handleClick, status, connectorType]);

  if (isConnected) {
    return (
      <ConnectorCard
        name={displayName}
        connectorType={connectorType}
        subtitle={syncSubtitle(lastPull, true)}
        logoUrl={logoUrl}
        tag={tag}
        status={connectionStatusBadge(status)}
        actions={
          <ConnectorActionsMenu
            name={displayName}
            items={[
              { label: 'Disconnect', onClick: handleDisconnect, danger: true },
            ]}
          />
        }
      />
    );
  }

  return (
    <ConnectorCard
      name={displayName}
      connectorType={connectorType}
      description={description}
      logoUrl={logoUrl}
      tag={tag}
      onSelect={handleAvailableClick}
      actions={
        <Button
          variant="light"
          size="compact-sm"
          onClick={handleAvailableClick}
        >
          Connect
        </Button>
      }
    />
  );
};

export default LeenConnectorTile;
